/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import {GoogleGenAI} from '@google/genai';
import {marked} from 'marked';

const ai = new GoogleGenAI({apiKey: import.meta.env.VITE_API_KEY});

const chat = ai.chats.create({
  model: 'gemini-2.0-flash-exp',
  config: {
    responseModalities: ['TEXT', 'IMAGE'],
  },
  history: [],
});

const userInput = document.querySelector('#input') as HTMLTextAreaElement;
const modelOutput = document.querySelector('#output') as HTMLDivElement;
const slideshow = document.querySelector('#slideshow') as HTMLDivElement;
const error = document.querySelector('#error') as HTMLDivElement;
const sendButton = document.querySelector('#send-button') as HTMLButtonElement;

const additionalInstructions = `
用一个关于许多小猫咪的有趣故事作为隐喻。
保持句子简短，但要口语化、自然且引人入胜。
为每句话生成一幅可爱、极简的黑白插画（白底黑线）。
图片中不应包含任何文字，除非是为了清晰度需要的小标签（如"A"、"B"或"1"、"2"等）。
无需额外说明，直接开始你的叙述。
持续进行直到完成。
 `;

async function addSlide(text: string, image: HTMLImageElement) {
  const slide = document.createElement('div');
  slide.className = 'slide';
  const caption = document.createElement('div') as HTMLDivElement;
  caption.innerHTML = await marked.parse(text);
  slide.append(image);
  slide.append(caption);
  slideshow.append(slide);
}

function parseError(error: unknown) {
  if (typeof error !== 'string') {
    return String(error);
  }

  const regex = /{"error":(.*)}/gm;
  const m = regex.exec(error);
  try {
    if (m && m[1]) {
      const e = m[1];
      const err = JSON.parse(e);
      return err.message;
    }
    return error;
  } catch (e) {
    return error;
  }
}

async function generate(message: string) {
  userInput.disabled = true;

  // 重置聊天，而不是直接访问私有属性
  const newChat = ai.chats.create({
    model: 'gemini-2.0-flash-exp',
    config: {
      responseModalities: ['TEXT', 'IMAGE'],
    },
    history: [],
  });

  // 替换全局chat变量
  Object.assign(chat, newChat);

  modelOutput.innerHTML = '';
  slideshow.innerHTML = '';
  error.innerHTML = '';
  error.toggleAttribute('hidden', true);

  try {
    const userTurn = document.createElement('div') as HTMLDivElement;
    userTurn.innerHTML = await marked.parse(message);
    userTurn.className = 'user-turn';
    modelOutput.append(userTurn);
    userInput.value = '';

    const result = await chat.sendMessageStream({
      message: message + additionalInstructions,
    });

    let text = '';
    let img = null;

    for await (const chunk of result) {
      if (!chunk.candidates || chunk.candidates.length === 0) {
        continue;
      }

      for (const candidate of chunk.candidates) {
        if (!candidate.content || !candidate.content.parts) {
          continue;
        }

        for (const part of candidate.content.parts) {
          if (part.text) {
            text += part.text;
          } else {
            try {
              const data = part.inlineData;
              if (data) {
                img = document.createElement('img');
                img.src = `data:image/png;base64,` + data.data;
              } else {
                console.log('no data', chunk);
              }
            } catch (e) {
              console.log('no data', chunk);
            }
          }
          if (text && img) {
            await addSlide(text, img);
            slideshow.removeAttribute('hidden');
            text = '';
            img = null;
          }
        }
      }
    }
    if (img) {
      await addSlide(text, img);
      slideshow.removeAttribute('hidden');
      text = '';
    }
  } catch (e) {
    const msg = parseError(e);
    error.innerHTML = `出现错误：${msg}`;
    error.removeAttribute('hidden');
  }
  userInput.disabled = false;
  userInput.focus();
}

userInput.addEventListener('keydown', async (e: KeyboardEvent) => {
  if (e.code === 'Enter') {
    e.preventDefault();
    const message = userInput.value;
    await generate(message);
  }
});

// 添加发送按钮点击事件
sendButton.addEventListener('click', async () => {
  const message = userInput.value;
  if (message.trim()) {
    await generate(message);
  }
});

const examples = document.querySelectorAll('#examples li');
examples.forEach((li) =>
  li.addEventListener('click', async () => {
    const content = li.textContent;
    if (content) {
      await generate(content);
    }
  }),
);
